import { Button } from "@mui/material";
import { IPagination, IPaginationRequest } from "interfaces/pagination";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { Map } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import Typography from "@mui/material/Typography";
import AvatarLetter from "components/common/AvatarLetter";
import Header from "components/common/Header";
import Table from "components/common/Table";
import withAuth from "hocs/withAuth";
import withPermission from "hocs/withPermission";
import useAlert from "hooks/useAlert";
import useToken from "hooks/useToken";
import { IStore } from "interfaces/store";
import Swal from "sweetalert2";
import CobraStoreServices from "services/cobraStore";

interface IStoreData {
  id: number;
  name: string;
  position: string;
  tel: string;
  url: string;
  last_edited_at: string;
  created_by: string;
  edited_by: string;
}

const ListStore = () => {
  const navigate = useNavigate();
  const { token } = useToken();
  const { onAlert } = useAlert();
  const [data, setData] = useState<{
    data: IStoreData[];
    pagination: IPagination;
  }>({
    data: [],
    pagination: {
      page: 1,
      total_item: 0,
      total_page: 0,
    },
  });
  const [pagination, setPagination] = useState<IPaginationRequest>({
    page: 1,
    per_page: 10,
  });
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const fetchData = useCallback(() => {
    CobraStoreServices.getAll(token as string, {
      per_page: pagination.per_page,
      page: pagination.page,
    })
      .then((res) => {
        let paginationRes: IPagination = res.data.pagination;
        setData({
          data: res.data.data.map((d: IStore) => {
            return {
              id: d.id,
              name: d.name,
              position: `${d.lat}, ${d.lng}`,
              // address: d.address,
              tel: d.tel,
              url: d.url,
              last_edited_at: d.last_edited_at,
              created_by: d.admin_created.name,
              edited_by: d.admin_edited.name,
            };
          }),
          pagination: {
            page: paginationRes.page,
            total_item: paginationRes.total_item,
            total_page: paginationRes.total_page,
          },
        });
        setIsLoading(false);
      })
      .catch((err) => {
        onAlert("error", err.response.data.message);
      });
  }, [token, onAlert, pagination]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onDelete = (item: IStore) => {
    Swal.fire({
      icon: "warning",
      html: `ต้องการลบร้าน <b>${item.name}</b> ใช่ หรือไม่?`,
      showCancelButton: true,
      confirmButtonText: "ลบ",
      cancelButtonText: "ยกเลิก",
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        CobraStoreServices.delete(token as string, item.id)
          .then((res) => {
            fetchData();
            onAlert("success", "ลบ Store สำเร็จ");
          })
          .catch((err) => {
            onAlert("error", err.response.data.message);
          });
      }
    });
  };

  return (
    <>
      <Header
        title="Store"
        breadcrumbs={[
          {
            label: "Dashboard",
            href: `/dashboard`,
          },
          {
            label: "Cobra",
            href: `/dashboard/cobra`,
          },
          {
            label: "Store",
            href: "/store/cobra/list",
          },
          {
            label: "List",
          },
        ]}
        actions={[
          <Link to={`/store/cobra/create`}>
            <Button variant="contained" size="large">
              <AddIcon
                sx={{
                  fontSize: 14,
                  mr: 1,
                }}
              />
              <Typography>Create</Typography>
            </Button>
          </Link>,
        ]}
      />
      <Table
        headers={[
          {
            key: "name",
            label: "Store name",
          },
          {
            key: "tel",
            label: "Tel",
          },
          {
            key: "url",
            label: "Map",
            render: (value) => (
              <a href={value} target="_blank" rel="noreferrer">
                <Button variant="contained" color="info">
                  <Map sx={{ mr: 1 }} />
                  <Typography>Map</Typography>
                </Button>
              </a>
            ),
          },
          {
            key: "created_by",
            label: "Created By",
            render: (value) => <AvatarLetter label={value} tooltip />,
          },
          {
            key: "edited_by",
            label: "Edited By",
            render: (value) => <AvatarLetter label={value} tooltip />,
          },
          {
            key: "last_edited_at",
            label: "Last Edited At",
            render: (value) => (
              <Typography>{new Date(value).toLocaleString("th-TH")}</Typography>
            ),
          },
        ]}
        actions={{
          onDetail: (item: any) => navigate(`/store/cobra/${item.id}`),
          onEdit: (item: any) => navigate(`/store/cobra/${item.id}/edit`),
          onDelete: (item: any) => onDelete(item),
        }}
        data={data.data}
        isLoading={isLoading}
        pagination={
          data.pagination
            ? {
                ...data.pagination,
                onChange: (page) =>
                  setPagination((pagination) => {
                    return {
                      ...pagination,
                      page,
                    };
                  }),
              }
            : undefined
        }
      />
    </>
  );
};

export default withAuth(withPermission(ListStore, "MANAGE_COBRA"));
